// ModelFort.jsx
import React, { useEffect } from "react";
import {
  Shield, Lock, Code, Cpu, Zap,
  BarChart2, Key, Eye, AlertTriangle,
  CheckCircle, ChevronRight, Target, Database, ArrowRight
} from "lucide-react";
import { motion, useAnimation } from "framer-motion";
import { useNavigate } from "react-router-dom";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};

const ModelFort = () => {
  const navigate = useNavigate();
  const controls = useAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
    controls.start('visible');
  }, [controls]);

  return (
    <div className="text-[#2c2c2c] overflow-x-hidden">

      {/* Hero Section */}
      <section className="relative pt-12 md:pt-16 pb-20 md:pb-24 px-6 sm:px-8 lg:px-12 overflow-hidden bg-white/40 border-b border-[#e5eef6]">
        <div className="relative z-10 max-w-6xl mx-auto">
          <motion.div
            className="flex flex-col lg:flex-row items-center justify-between gap-16"
            initial="hidden"
            animate={controls}
            variants={staggerContainer}
          >
            <motion.div className="lg:w-1/2 space-y-6" variants={fadeInUp}>
              <motion.div
                className="inline-flex items-center space-x-2 bg-[#005a9c]/10 px-4 py-1.5 rounded-full border border-[#005a9c]/20"
                variants={fadeInUp}
              >
                <Database className="w-4 h-4 text-[#005a9c]" />
                <span className="text-[#005a9c] text-sm font-medium tracking-wider uppercase">MODELFORT</span>
              </motion.div>

              <motion.h1
                className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight text-slate-900"
                variants={fadeInUp}
              >
                Protect Your AI Models <span className="text-[#005a9c]">From Attacks</span>
              </motion.h1>

              <motion.p
                className="text-lg text-slate-600 leading-relaxed max-w-xl"
                variants={fadeInUp}
              >
                ModelFort is the industry's most advanced ML model scanner, providing comprehensive analysis
                of various model formats to detect and prevent security vulnerabilities before deployment.
              </motion.p>

              <motion.div
                className="flex flex-col sm:flex-row gap-4 pt-2"
                variants={fadeInUp}
              >
                <motion.button
                  onClick={() => navigate('/contactus')}
                  className="group relative bg-[#005a9c] text-white font-bold px-8 py-3.5 rounded-lg overflow-hidden transition-all duration-300 hover:bg-[#003d6b] flex items-center justify-center shadow-lg"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="relative z-10 flex items-center">
                    <span>REQUEST DEMO</span>
                    <ChevronRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.button>

                <motion.button
                  onClick={() => navigate('/aboutus')}
                  className="flex items-center justify-center px-6 py-3.5 text-[#005a9c] font-medium rounded-lg border-2 border-[#005a9c] hover:bg-[#005a9c]/5 transition-colors"
                  whileHover={{ y: -2 }}
                >
                  <span className="flex items-center">
                    <span>LEARN MORE</span>
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </span>
                </motion.button>
              </motion.div>

              {/* Trust Badges */}
              <motion.div
                className="mt-8 flex flex-wrap items-center justify-center sm:justify-start gap-6 opacity-80"
                variants={fadeInUp}
              >
                <div className="flex items-center space-x-2">
                  <Shield className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-slate-600">Enterprise-Grade Security</span>
                </div>
                <div className="h-6 w-px bg-slate-300"></div>
                <div className="flex items-center space-x-2">
                  <Target className="w-5 h-5 text-blue-600" />
                  <span className="text-sm text-slate-600">AI-Powered Analysis</span>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              className="lg:w-1/2 relative"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative z-10 bg-white/80 backdrop-blur-lg rounded-2xl p-8 border border-[#e5eef6] shadow-2xl">
                <div className="relative">
                  <h3 className="text-xl font-bold mb-6 text-slate-900 flex items-center">
                    <Database className="w-6 h-6 mr-2 text-[#005a9c]" />
                    Model Analysis
                  </h3>
                  <div className="space-y-6">
                    {[
                      { label: 'Model Coverage', value: '95%', color: 'bg-[#005a9c]' },
                      { label: 'Vulnerability Detection', value: '98%', color: 'bg-green-500' },
                      { label: 'False Positive Rate', value: '1.2%', color: 'bg-yellow-500', isInverse: true }
                    ].map((item, index) => (
                      <div key={index}>
                        <div className="flex justify-between text-sm text-slate-600 mb-1.5">
                          <span>{item.label}</span>
                          <span className="font-semibold">{item.value}</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <motion.div
                            className={`h-full ${item.color} rounded-full`}
                            initial={{ width: 0 }}
                            animate={{ width: item.isInverse ? '10%' : item.value }}
                            transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                          ></motion.div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 pt-6 border-t border-slate-100">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-[#005a9c]">50+</div>
                        <div className="text-xs text-slate-500">Formats</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-[#005a9c]">99.9%</div>
                        <div className="text-xs text-slate-500">Accuracy</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-[#005a9c]">24/7</div>
                        <div className="text-xs text-slate-500">Active</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="relative py-24 px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 bg-[#f0f7ff] border-b border-[#e5eef6]">
        <div className="relative z-10 max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp}>
              <div className="inline-flex items-center space-x-2 bg-yellow-400/10 px-4 py-1.5 rounded-full border border-yellow-400/20 mb-6">
                <Zap className="w-4 h-4 text-yellow-600" />
                <span className="text-yellow-600 text-sm font-medium tracking-wider uppercase">Our Approach</span>
              </div>
              <h2 className="text-4xl font-bold mb-6 text-slate-900">
                Detect, Respond, and <span className="text-[#005a9c]">Innovate</span>
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
                RakFort's Model Scanner secures your digital supply chain before deployment,
                reducing the risk of adversarial code and ensuring AI model integrity.
              </p>
            </motion.div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Eye className="w-8 h-8 text-[#005a9c]" />,
                title: "Detect",
                description: "Identify vulnerabilities and threats in your ML models before deployment."
              },
              {
                icon: <AlertTriangle className="w-8 h-8 text-[#005a9c]" />,
                title: "Respond",
                description: "Quickly address and mitigate identified risks with actionable insights."
              },
              {
                icon: <CheckCircle className="w-8 h-8 text-[#005a9c]" />,
                title: "Innovate",
                description: "Deploy with confidence and focus on building better AI solutions."
              }
            ].map((item, index) => (
              <motion.div 
                key={index} 
                className="rf-card p-10 text-center bg-white"
                whileHover={{ y: -5 }}
              >
                <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  {item.icon}
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-24 bg-transparent border-b border-[#e5eef6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center space-x-2 bg-[#005a9c]/10 px-4 py-2 rounded-full border border-[#005a9c]/30 mb-6">
              <Code className="w-5 h-5 text-[#005a9c]" />
              <span className="text-[#005a9c] font-medium text-sm tracking-wide uppercase">Key Features</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Advanced Model Protection <span className="text-[#005a9c]">Capabilities</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              {[
                {
                  icon: <Cpu className="w-6 h-6 text-[#005a9c]" />,
                  title: "Leading Model Formats",
                  description: "Compatible with PyTorch, TensorFlow, XGBoost, Keras, and other popular frameworks."
                },
                {
                  icon: <Lock className="w-6 h-6 text-[#005a9c]" />,
                  title: "Secure Gateway",
                  description: "Endpoint that captures and verifies MLOps team's model requests before delivery."
                },
                {
                  icon: <Shield className="w-6 h-6 text-[#005a9c]" />,
                  title: "Policy Engine",
                  description: "Built-in policies for model acceptance/rejection with comprehensive security checks."
                },
                {
                  icon: <Zap className="w-6 h-6 text-[#005a9c]" />,
                  title: "Automated Scanning",
                  description: "Streamline model validation with automated security scanning processes."
                }
              ].map((feature, index) => (
                <div
                  key={index}
                  className="rf-card p-6 hover:border-[#005a9c]/30 group transition-all"
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center border border-slate-100 group-hover:bg-[#005a9c]/10 transition-colors">
                        {feature.icon}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="relative">
              <div className="relative bg-white p-2 rounded-2xl border border-[#e5eef6] shadow-xl">
                <img
                  src="https://cdn.dribbble.com/users/211337/screenshots/16975402/media/6b34b7bfe4ac11a6714026b724f4a819.png"
                  alt="ModelFort Dashboard"
                  className="rounded-lg w-full h-auto opacity-90"
                />
                <div className="absolute -bottom-4 -right-4 bg-yellow-400 text-[#2c2c2c] font-bold px-4 py-2 rounded-lg shadow-lg border border-yellow-500/20">
                  Real-time Monitoring
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LLM Gateway Features */}
      <section className="py-24 bg-[#f0f7ff] border-b border-[#e5eef6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center space-x-2 bg-[#005a9c]/10 px-4 py-2 rounded-full border border-[#005a9c]/30 mb-6">
              <Key className="w-5 h-5 text-[#005a9c]" />
              <span className="text-[#005a9c] font-medium text-sm tracking-wide uppercase">LLM Gateway</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Enterprise-Grade <span className="text-[#005a9c]">LLM Gateway</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Secure, monitor, and optimize your AI model deployments with our comprehensive LLM Gateway solution.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-stretch">
            <div className="space-y-8">
              <div className="rf-card p-8 h-full bg-white">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center border border-slate-100 shrink-0">
                    <BarChart2 className="w-6 h-6 text-[#005a9c]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">Unified Access & Analytics</h3>
                    <p className="text-slate-600 leading-relaxed text-sm">
                      Single interface for all your LLM needs with comprehensive analytics to track usage,
                      performance, and costs across all your AI models and services.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rf-card p-8 h-full bg-white">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center border border-slate-100 shrink-0">
                    <Shield className="w-6 h-6 text-[#005a9c]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">Advanced Security</h3>
                    <ul className="grid sm:grid-cols-2 gap-3 mt-4">
                      {[
                        "Centralized key management",
                        "Role-based access (RBAC)",
                        "Enterprise SSO integration",
                        "Compliance audit logging"
                      ].map((item, i) => (
                        <li key={i} className="flex items-start text-xs font-medium text-slate-600">
                          <CheckCircle className="w-4 h-4 text-green-600 mr-2 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="rf-card p-8 bg-white">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center border border-slate-100 shrink-0">
                  <Zap className="w-6 h-6 text-[#005a9c]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">Performance & Monitoring</h3>
                  <div className="space-y-4 mt-6">
                    {[
                      {
                        title: "Real-time Monitoring",
                        description: "Track model performance metrics including latency and throughput.",
                        icon: <BarChart2 className="w-5 h-5 text-[#005a9c]" />
                      },
                      {
                        title: "Usage Analytics",
                        description: "Gain insights into model usage patterns and optimize resource allocation.",
                        icon: <Code className="w-5 h-5 text-[#005a9c]" />
                      },
                      {
                        title: "Cost Management",
                        description: "Monitor and control AI spending with detailed cost breakdowns.",
                        icon: <Key className="w-5 h-5 text-[#005a9c]" />
                      }
                    ].map((item, i) => (
                      <div key={i} className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center mt-0.5 shrink-0 border border-slate-100 shadow-sm">
                          {item.icon}
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900 text-sm">{item.title}</h4>
                          <p className="text-xs text-slate-600 leading-relaxed">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#005a9c] text-white">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Secure Your AI Models?
          </h2>
          <p className="text-lg text-blue-100 mb-10 max-w-2xl mx-auto leading-relaxed">
            Join leading enterprises that trust ModelFort to protect their AI infrastructure and ensure secure model deployments.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => navigate('/contactus')}
              className="bg-white text-[#005a9c] font-bold px-8 py-4 rounded-lg transition-all duration-300 shadow-xl hover:bg-slate-50"
            >
              Get Started or Schedule a Demo
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ModelFort;
